import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart } from 'lucide-react';
import { LogoFull, BRAND_COLORS } from '../components/brand';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const programs = [
    { key: 'oursquare', label: 'Our Square', subtitle: 'Transitional Living', logo: '/images/our-square-logo.png' },
    { key: 'heat_men', label: 'H.E.A.T.', subtitle: 'For Men', logo: '/images/heat-logo.png' },
    { key: 'heat_her', label: 'H.E.A.T.', subtitle: 'For Her', logo: '/images/heat-logo.png' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - branding with predominant logo */}
      <div 
        className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative overflow-hidden"
        style={{ background: `linear-gradient(to bottom, ${BRAND_COLORS.primary}, #007A87)` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col items-center text-center max-w-2xl">
          {/* Organization Logos */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl mb-8 w-full max-w-xl">
            <div className="flex justify-center mb-6">
              <Link
                to="/register"
                data-testid="purpose-driven-logo-link"
                aria-label="Sign up for Purpose Driven Peer Support"
                title="Sign up as a Purpose Driven Peer Support participant"
                className="inline-block transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-xl"
              >
                <LogoFull size="xl" brand="purpose-driven" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-5 border-t border-slate-200">
              {programs.map((prog) => (
                <button
                  key={prog.key}
                  data-testid={`program-${prog.key}`}
                  onClick={() => setSelectedProgram(prog.key)}
                  className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl transition-all duration-200 cursor-pointer ${
                    selectedProgram === prog.key
                      ? 'bg-primary/10 ring-2 ring-primary/40 scale-105'
                      : 'hover:bg-slate-50 hover:scale-105'
                  }`}
                >
                  <div className="w-[96px] h-[96px] flex items-center justify-center">
                    <img src={prog.logo} alt={prog.label} className="w-[96px] h-[96px] object-contain" />
                  </div>
                  <span className="text-xs text-slate-700 font-semibold leading-tight">{prog.label}</span>
                  <span className="text-[10px] text-slate-400 leading-tight">{prog.subtitle}</span>
                </button>
              ))}
            </div>
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            Supporting your journey, every step of the way
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-md">
            A secure platform connecting individuals in recovery with peer support specialists, coordinators, and the resources they need to thrive.
          </p>
          <div className="mt-6 flex items-center gap-2 text-white/60 text-xs">
            <span>Purpose Driven</span><span>|</span>
            <span>Our Square</span><span>|</span>
            <span>HEAT for Men</span><span>|</span>
            <span>HEAT for Her</span>
          </div>
        </div>
      </div>

      {/* Right side - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo - prominent */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <Link
              to="/register"
              data-testid="purpose-driven-logo-link-mobile"
              aria-label="Sign up for Purpose Driven Peer Support"
              title="Sign up as a Purpose Driven Peer Support participant"
              className="inline-block transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-xl"
            >
              <LogoFull size="lg" />
            </Link>
            <div className="flex items-center justify-center gap-4 mt-5">
              {programs.map((prog) => (
                <button
                  key={prog.key}
                  onClick={() => setSelectedProgram(prog.key)}
                  className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all ${
                    selectedProgram === prog.key ? 'bg-primary/10 ring-2 ring-primary/30 scale-105' : 'hover:bg-muted hover:scale-105'
                  }`}
                >
                  <div className="w-[80px] h-[80px] flex items-center justify-center">
                    <img src={prog.logo} alt={prog.label} className="w-[80px] h-[80px] object-contain" />
                  </div>
                  <span className="text-[10px] text-slate-600 font-medium">{prog.label}</span>
                  <span className="text-[8px] text-muted-foreground">{prog.subtitle}</span>
                </button>
              ))}
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>Welcome back</h2>
          <p className="text-muted-foreground text-sm mb-2">Sign in to continue your journey</p>

          {selectedProgram && (
            <div className="mb-6 flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 border border-primary/15" data-testid="selected-program-badge">
              <img
                src={programs.find(p => p.key === selectedProgram)?.logo}
                alt=""
                className="w-6 h-6 object-contain"
              />
              <span className="text-sm font-medium text-foreground">
                {programs.find(p => p.key === selectedProgram)?.label}{' '}
                <span className="text-muted-foreground font-normal">{programs.find(p => p.key === selectedProgram)?.subtitle}</span>
              </span>
              <button onClick={() => setSelectedProgram(null)} className="ml-auto text-muted-foreground hover:text-foreground text-xs">✕</button>
            </div>
          )}

          {!selectedProgram && <div className="mb-6" />}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="h-11 pr-10"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium" disabled={loading} data-testid="login-submit-button">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-center -mt-1">
              <Link to="/forgot-password" className="text-xs text-teal-600 hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-teal-600 font-medium hover:underline" data-testid="register-link">Create one</Link>
          </p>

          <div className="mt-4 text-center">
            <Link 
              to="/demo" 
              className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:text-teal-700 hover:underline"
              data-testid="demo-mode-link"
            >
              <Heart size={14} /> Try Demo Mode — explore all roles instantly
            </Link>
          </div>

          <div className="mt-3 flex flex-col items-center gap-2 text-sm">
            <Link to="/refer" className="text-sky-600 hover:underline font-medium" data-testid="public-referral-link">Refer Someone for Support</Link>
            <Link to="/submit-resource" className="text-emerald-600 hover:underline font-medium" data-testid="public-resource-link">Share a Community Resource</Link>
          </div>

          {/* Demo accounts visible only on non-production environments */}
          {!window.location.hostname.includes('purposedrivenpeersupport.com') && (
          <div className="mt-6 p-3 rounded-xl bg-muted/50 border border-border">
            <p className="text-xs text-muted-foreground font-medium mb-2">Demo Accounts (preview only):</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>Client: client@purposedriven.org / client123</p>
              <p>Peer Specialist: peer@purposedriven.org / peer123</p>
              <p>Facilitator: facilitator@heat.com / facilitator123</p>
              <p>Coordinator: coordinator@purposedriven.org / coord123</p>
              <p>Referral Source: court@purposedriven.org / court123</p>
              <p>Admin (ED): admin@purposedriven.org / admin123</p>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
